/**
 * 飞书文档展示模型与发送块构造。
 *
 * 约定（与后端 document 内容块协议对齐）：
 * - 搜索接口的 title_highlighted 含 <em> 标签，只解析为高亮片段，禁止 v-html；
 * - metadata 只写 fileName/source/docType/ownerName/updateTime，不写 token；
 * - docType 未知时省略，绝不冒充 docx（WIKI 等会被错误路由）；
 * - source.value 必须通过 http(s) + 飞书域名双校验，历史/发送两处同规则。
 */
import type { LarkDocItemDto } from './api'

/** 单次发送上限：对齐后端 agi.file-input.max-files-per-request（默认 10，请求级累计） */
export const FEISHU_DOC_MAX = 10

export const FEISHU_DOC_FALLBACK_TITLE = '飞书文档'

export type LarkDocType = 'docx' | 'doc' | 'sheet' | 'bitable'

const LARK_DOC_TYPE_LABELS: Record<LarkDocType, string> = {
  docx: 'DOCX',
  doc: 'DOC',
  sheet: 'SHEET',
  bitable: 'BITABLE',
}

/** URL 路径 → 文档类型；/wiki/ 是知识库节点，无法据此判断真实文档类型 */
const URL_PATH_DOC_TYPES: Record<string, LarkDocType> = {
  docx: 'docx',
  docs: 'doc',
  sheets: 'sheet',
  base: 'bitable',
}

const LARK_DOC_PATHS = new Set([...Object.keys(URL_PATH_DOC_TYPES), 'wiki'])

const LARK_HOST_SUFFIXES = ['feishu.cn', 'larksuite.com']

export interface LarkTitleSegment {
  text: string
  highlight: boolean
}

export interface LarkTitle {
  text: string
  segments: LarkTitleSegment[]
}

/**
 * 飞书文档视图模型。token/entityType 只服务于选择期去重与标题清洗，
 * 不进入消息块。
 */
export interface LarkDocView {
  /** 去重键：entityType + token */
  key: string
  entityType: 'DOC' | 'WIKI'
  token: string
  title: string
  titleSegments: LarkTitleSegment[]
  url: string
  docType?: LarkDocType
  ownerName?: string
  updateTime?: number
  isWiki: boolean
  /** 行内类型角标（DOCX/DOC/SHEET/BITABLE），未知类型回退“文档” */
  typeLabel: string
}

/** 发送侧内容块（结构对齐 AG-UI document part） */
export interface LarkDocumentBlock {
  type: 'document'
  source: { type: 'url'; value: string }
  metadata: {
    fileName: string
    source: 'lark'
    docType?: LarkDocType
    ownerName?: string
    updateTime?: number
  }
}

export type UserContentPart = { type: 'text'; text: string } | LarkDocumentBlock

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: '\'',
  nbsp: ' ',
}

/** 只解码常见命名实体与数字实体；未知实体保持原样，避免吞掉正文 */
function decodeEntities(raw: string): string {
  return raw.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === '#') {
      const hex = entity[1]?.toLowerCase() === 'x'
      const code = Number.parseInt(entity.slice(hex ? 2 : 1), hex ? 16 : 10)
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return match
      try {
        return String.fromCodePoint(code)
      } catch {
        return match
      }
    }
    return NAMED_ENTITIES[entity.toLowerCase()] ?? match
  })
}

/**
 * title_highlighted → 纯文本 + 高亮片段。
 *
 * 只把 <em>/</em> 当作高亮边界，其余标签一律剥掉；实体解码后再输出，
 * 因此组件只需文本插值，天然免疫 HTML 注入。
 */
export function parseLarkHighlight(raw?: string | null): LarkTitle {
  if (!raw) return { text: '', segments: [] }

  const parts = raw.split(/(<em>|<\/em>)/i)
  const segments: LarkTitleSegment[] = []
  let highlight = false

  const push = (text: string, hit: boolean) => {
    if (!text) return
    const last = segments[segments.length - 1]
    // 合并相邻同态片段，减少渲染节点
    if (last && last.highlight === hit) last.text += text
    else segments.push({ text, highlight: hit })
  }

  for (const part of parts) {
    const token = part.toLowerCase()
    if (token === '<em>') {
      highlight = true
      continue
    }
    if (token === '</em>') {
      highlight = false
      continue
    }
    const text = decodeEntities(part.replace(/<[^>]*>/g, ''))
    push(text, highlight)
  }

  return { text: segments.map((segment) => segment.text).join(''), segments }
}

/** 剥离任意标签并解码实体的纯文本入口（owner 等自由文本同样可用） */
export function plainText(raw?: string | null): string {
  return parseLarkHighlight(raw).text
}

function parseUrl(raw?: string | null): URL | null {
  if (typeof raw !== 'string' || !raw.trim()) return null
  try {
    return new URL(raw.trim())
  } catch {
    return null
  }
}

export function isFeishuHost(hostname: string): boolean {
  const host = hostname.toLowerCase()
  return LARK_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))
}

/** URL 协议白名单（所有外链打开前都必须过这一层） */
export function safeExternalUrl(raw?: string | null): string | null {
  const url = parseUrl(raw)
  if (!url || (url.protocol !== 'https:' && url.protocol !== 'http:')) return null
  return url.href
}

/** 飞书文档 URL：http(s) + 飞书域名 + 已知文档路径 */
export function isLarkDocUrl(raw?: string | null): boolean {
  const url = parseUrl(raw)
  if (!url || (url.protocol !== 'https:' && url.protocol !== 'http:')) return false
  if (!isFeishuHost(url.hostname)) return false
  const segments = url.pathname.split('/').filter(Boolean)
  return segments.length >= 2 && LARK_DOC_PATHS.has(segments[0])
}

/** 从文档 URL 提取 token；/wiki/ 是节点 token，不能当作文档 token 返回 */
export function larkTokenFromUrl(raw?: string | null): string | null {
  const url = parseUrl(raw)
  if (!url) return null
  const segments = url.pathname.split('/').filter(Boolean)
  if (segments.length < 2 || !URL_PATH_DOC_TYPES[segments[0]]) return null
  return segments[1] || null
}

export function inferLarkDocType(raw?: string | null): LarkDocType | undefined {
  const url = parseUrl(raw)
  if (!url) return undefined
  const first = url.pathname.split('/').filter(Boolean)[0]
  return first ? URL_PATH_DOC_TYPES[first] : undefined
}

/** 后端 doc_type 可能为空或逗号多值，小写归一后取首个可识别值 */
export function normalizeLarkDocType(raw?: string | null): LarkDocType | undefined {
  if (!raw) return undefined
  for (const part of raw.split(',')) {
    const value = part.trim().toLowerCase()
    if (value in LARK_DOC_TYPE_LABELS) return value as LarkDocType
  }
  return undefined
}

/**
 * 时间戳秒/毫秒归一：后端 update_time 两种粒度都存在（飞书原生为秒级），
 * 小于 1e11 的正数按秒处理（毫秒级最早有效值也在 1e12 量级，1e11 ms ≈
 * 1973 年，不会误伤），统一 ×1000 归一为毫秒。本函数是 update_time 的
 * 唯一换算点，下游（formatRelativeTime 相对时间、消息块 metadata）均按
 * 毫秒消费，不存在双重换算。
 */
const SECONDS_TIMESTAMP_THRESHOLD = 1e11

function toTimestamp(raw?: number | string | null): number | undefined {
  const normalize = (value: number): number | undefined => {
    if (!Number.isFinite(value) || value <= 0) return undefined
    return value < SECONDS_TIMESTAMP_THRESHOLD ? value * 1000 : value
  }
  if (typeof raw === 'number') return normalize(raw)
  if (typeof raw === 'string' && raw.trim()) return normalize(Number(raw))
  return undefined
}

function cleanTitle(raw: string | undefined, token: string): string {
  const text = plainText(raw).trim()
  // 只在空标题或「后端把标题回退成了 token」时兜底，与 token 精确比对。
  // 刻意不做 token 形状猜测：16+ 位字母/数字/下划线/横线的合法文档标题
  // （Q3_Report_Final_2026、implementationdoc 等）会被形状正则误伤成兜底文案
  // （2026-10-27 实测事故，发起人定位）。
  if (!text || text === token) return FEISHU_DOC_FALLBACK_TITLE
  return text
}

/** 搜索 DTO → 视图模型；URL 不合规的条目直接丢弃，避免进入选择态 */
export function toLarkDocView(dto: LarkDocItemDto): LarkDocView | null {
  const url = safeExternalUrl(dto.url)
  if (!url || !isLarkDocUrl(url)) return null

  const token = dto.token?.trim() || larkTokenFromUrl(url) || url
  const entityType = dto.entity_type?.toUpperCase() === 'WIKI' || dto.entityType?.toUpperCase() === 'WIKI'
    ? 'WIKI'
    : 'DOC'
  const rawTitle = dto.title ?? dto.title_highlighted ?? dto.titleHighlighted
  const title = cleanTitle(rawTitle, token)
  const parsedHighlight = parseLarkHighlight(dto.title_highlighted ?? dto.titleHighlighted)

  // 只有高亮文本与最终标题一致时才保留 <em> 片段；否则用纯文本单段，
  // 防止“标题被 token 兜底后仍展示高亮残片”的错位
  const titleSegments: LarkTitleSegment[] = parsedHighlight.text === title
    ? parsedHighlight.segments
    : [{ text: title, highlight: false }]

  const docType = normalizeLarkDocType(dto.doc_type ?? dto.docType) ?? inferLarkDocType(url)
  const ownerName = plainText(dto.owner_name ?? dto.ownerName).trim() || undefined
  const updateTime = toTimestamp(dto.update_time ?? dto.updateTime)

  return {
    key: `${entityType}:${token}`,
    entityType,
    token,
    title,
    titleSegments,
    url,
    docType,
    ownerName,
    updateTime,
    isWiki: entityType === 'WIKI',
    typeLabel: docType ? LARK_DOC_TYPE_LABELS[docType] : '文档',
  }
}

/** 壳桥 tt.docsPicker 返回条目。官方仅两字段：filePath = 文档 URL
 *  （https://<租户>.feishu.cn/{docx|docs|sheets|base|wiki}/<token>），
 *  fileName = 文档名；无类型/作者/更新时间等附加字段 */
export interface DocsPickerItem {
  filePath?: unknown
  fileName?: unknown
}

/**
 * 壳桥 docsPicker 条目 → 视图模型（对齐 toLarkDocView 的清洗规则）。
 * filePath 是承重字段（发送块 source 与后端读取都走它）：缺失或非飞书
 * 文档 URL 的条目返回 null 丢弃，由调用方决定回退。token 取 URL 第二段
 * （wiki 时即节点 token，与搜索流 WIKI 实体的 token 语义一致，仅用于
 * 去重与标题清洗）；条目无类型字段，docType 从 URL 路径推导。
 */
export function docsPickerItemToLarkDocView(item: DocsPickerItem): LarkDocView | null {
  const url = safeExternalUrl(typeof item.filePath === 'string' ? item.filePath : null)
  if (!url || !isLarkDocUrl(url)) return null

  const segments = parseUrl(url)?.pathname.split('/').filter(Boolean) ?? []
  const token = segments[1] || null
  if (!token) return null

  const isWiki = segments[0] === 'wiki'
  const entityType: 'DOC' | 'WIKI' = isWiki ? 'WIKI' : 'DOC'
  const title = cleanTitle(typeof item.fileName === 'string' ? item.fileName : undefined, token)
  const docType = inferLarkDocType(url)

  return {
    key: `${entityType}:${token}`,
    entityType,
    token,
    title,
    titleSegments: [{ text: title, highlight: false }],
    url,
    docType,
    isWiki,
    typeLabel: docType ? LARK_DOC_TYPE_LABELS[docType] : '文档',
  }
}

/** 发送时以选择态 token 再清洗一次标题（新数据的唯一权威清洗点） */
function sendingTitle(doc: LarkDocView): string {
  const text = doc.title.trim()
  // 判定口径与 cleanTitle 一致：只认空标题与「标题即 token」，不做形状猜测
  if (!text || text === doc.token) return FEISHU_DOC_FALLBACK_TITLE
  return text
}

/**
 * 构造用户消息内容块：text 在前、document 随后。
 * 任何一条文档 URL 失效都不静默少发——skipped > 0 由调用方阻止发送。
 */
export function buildUserContent(
  text: string,
  docs: LarkDocView[],
): { content: UserContentPart[]; skipped: number } {
  const content: UserContentPart[] = []
  if (text.trim()) content.push({ type: 'text', text })

  const keys = new Set<string>()
  let skipped = 0

  for (const doc of docs) {
    const url = safeExternalUrl(doc.url)
    if (!url || !isLarkDocUrl(url) || keys.has(doc.key)) {
      skipped += 1
      continue
    }
    keys.add(doc.key)

    const block: LarkDocumentBlock = {
      type: 'document',
      source: { type: 'url', value: url },
      metadata: {
        fileName: sendingTitle(doc),
        source: 'lark',
        ...(doc.docType ? { docType: doc.docType } : {}),
        ...(doc.ownerName ? { ownerName: doc.ownerName } : {}),
        ...(doc.updateTime ? { updateTime: doc.updateTime } : {}),
      },
    }
    content.push(block)
  }

  return { content, skipped }
}

/** 结果行时间：刚刚 / n 分钟前 / n 小时前 / n 天前 / YYYY-MM-DD */
export function formatRelativeTime(timestamp?: number, now = Date.now()): string {
  if (!timestamp || !Number.isFinite(timestamp)) return ''
  const diff = now - timestamp
  if (diff < 60_000) return '刚刚'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)} 天前`

  const date = new Date(timestamp)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
